import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CalendarClock, Plus, Smartphone, Store, Users } from "lucide-react";
import { adminGetOverview } from "../api/admin.js";
import { Badge } from "../components/ui/Badge.js";
import { Button } from "../components/ui/Button.js";
import { Table, TBody, THead, TR, TH, TD, EmptyRow } from "../components/ui/Table.js";
import { FullPageSpinner } from "../components/ui/Spinner.js";
import { Alert } from "../components/ui/Alert.js";
import { MetricCard, PageHeader, SectionHeader, SectionSurface } from "../components/PageScaffold.js";
import { CloudApiError, describeError } from "../lib/errors.js";
import { formatDateTime, formatNumber } from "../lib/format.js";
import {
  formatLicensePlan,
  getLicenseExpiryState,
  licenseStatusLabel,
  licenseStatusTone
} from "../lib/license.js";

export function TenantListPage() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => adminGetOverview()
  });

  if (isLoading) return <FullPageSpinner />;
  if (isError) {
    const message = error instanceof CloudApiError ? describeError(error.code) : "加载失败";
    return <Alert tone="danger">{message}</Alert>;
  }

  const tenants = data?.tenants ?? [];
  const now = new Date();
  const expiredCount = tenants.filter((tenant) => {
    const expiryState = getLicenseExpiryState(tenant.license.expiresAt, tenant.license.status, now);
    return expiryState.daysRemaining <= 0 || tenant.license.status === "expired";
  }).length;
  const dueSoonCount = tenants.filter((tenant) => {
    const expiryState = getLicenseExpiryState(tenant.license.expiresAt, tenant.license.status, now);
    return expiryState.daysRemaining > 0 && expiryState.daysRemaining <= 7;
  }).length;
  const storeCount = tenants.reduce((total, tenant) => total + (tenant.storeCount ?? 0), 0);

  return (
    <div>
      <PageHeader
        eyebrow="客户运营 / 客户管理"
        title="客户管理"
        description="管理客户档案、登录账号、套餐授权、设备上限和店铺额度。"
        actions={
          <Link to="/tenants/new">
            <Button>
              <Plus className="h-4 w-4" />
              新增客户
            </Button>
          </Link>
        }
      />

      <section className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="客户总数" value={formatNumber(tenants.length)} detail="全部客户档案" icon={<Users className="h-4 w-4" />} />
        <MetricCard
          label="7 天内到期"
          value={formatNumber(dueSoonCount)}
          detail={`${formatNumber(expiredCount)} 个已过期`}
          tone={expiredCount > 0 ? "danger" : "warning"}
          icon={<CalendarClock className="h-4 w-4" />}
        />
        <MetricCard
          label="活跃设备"
          value={formatNumber(data?.totals.activeDeviceCount)}
          detail={`${formatNumber(data?.totals.revokedDeviceCount)} 个已撤销`}
          tone="success"
          icon={<Smartphone className="h-4 w-4" />}
        />
        <MetricCard label="已建店铺" value={formatNumber(storeCount)} detail="所有客户累计店铺" icon={<Store className="h-4 w-4" />} />
      </section>

      <SectionSurface>
        <SectionHeader title="客户列表" description="优先在工作台处理到期和异常；列表用于查找客户并进入客户档案。" />
        <Table className="rounded-none border-0">
          <THead>
            <TR>
              <TH>客户</TH>
              <TH>ID</TH>
              <TH>授权状态</TH>
              <TH>登录邮箱</TH>
              <TH>设备</TH>
              <TH>店铺</TH>
              <TH>到期时间</TH>
            </TR>
          </THead>
          <TBody>
            {tenants.length === 0 ? (
              <EmptyRow colSpan={7} label="暂无客户。" />
            ) : (
              tenants.map((tenant) => {
                const expiryState = getLicenseExpiryState(tenant.license.expiresAt, tenant.license.status);
                return (
                  <TR key={tenant.id}>
                    <TD>
                      <Link
                        to={`/tenants/${encodeURIComponent(tenant.id)}`}
                        className="font-medium text-text-primary hover:text-accent"
                      >
                        {tenant.name}
                      </Link>
                    </TD>
                    <TD className="font-mono text-xs text-text-secondary">{tenant.id}</TD>
                    <TD>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge tone={licenseStatusTone(tenant.license.status)}>
                          {licenseStatusLabel(tenant.license.status)}
                        </Badge>
                        <span className="text-xs text-text-secondary">{formatLicensePlan(tenant.license.plan)}</span>
                      </div>
                    </TD>
                    <TD>
                      <span className="text-text-primary">{tenant.ownerEmail ?? "—"}</span>
                    </TD>
                    <TD>
                      <span className="text-text-primary">{formatNumber(tenant.activeDeviceCount)}</span>
                      <span className="text-text-muted"> / {formatNumber(tenant.license.deviceLimit)}</span>
                    </TD>
                    <TD>
                      <span className="text-text-primary">{formatNumber(tenant.storeCount ?? 0)}</span>
                      <span className="text-text-muted"> / {formatNumber(tenant.license.storeLimit ?? 50)}</span>
                    </TD>
                    <TD>
                      <div className="whitespace-nowrap text-xs text-text-secondary">
                        {formatDateTime(tenant.license.expiresAt)}
                      </div>
                      <Badge tone={expiryState.tone} className="mt-1">
                        {expiryState.label}
                      </Badge>
                    </TD>
                  </TR>
                );
              })
            )}
          </TBody>
        </Table>
      </SectionSurface>
    </div>
  );
}
